import React,{useRef,useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useTheme} from '../contexts/ThemeContext';
import {useSettings} from '../contexts/SettingsContext';
import {useBookmarks} from '../contexts/BookmarkContext';
import {exportBookmarks,importBookmarks,downloadFile} from '../utils/import-export';
import {resolveBestFaviconUrl} from '../utils/favicon';

const {
  FiArrowLeft,FiRotateCcw,FiDownload,FiUpload,FiCopy,FiCheck,FiBookmark,FiShield,FiSmartphone,FiMonitor,FiFileText,FiDatabase,FiRefreshCw,FiInfo,FiImage,FiX
}=FiIcons;

const emptyFaviconProgress={running:false,processed:0,total:0,updated:0,current:0,unavailable:0,manual:0,cancelled:false};

const Settings=()=> {
  const navigate=useNavigate();
  const restoreInputRef=useRef(null);
  const faviconRefreshCancelRef=useRef(false);
  const [status,setStatus]=useState('');
  const [busy,setBusy]=useState(false);
  const [copiedCapture,setCopiedCapture]=useState(false);
  const [faviconProgress,setFaviconProgress]=useState(emptyFaviconProgress);
  const {theme,highContrast,toggleTheme,toggleHighContrast}=useTheme();
  const {settings,updateSetting,resetSettings}=useSettings();
  const {bookmarks,folders,importLibrary,updateBookmark,syncStatus,pendingChanges,isOnline}=useBookmarks();

  const cardClass=`p-5 sm:p-6 rounded-2xl border ${theme==='dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'}`;
  const inputClass=`min-h-11 px-3 py-2 rounded-xl border text-base sm:text-sm ${theme==='dark' ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'}`;

  const notify=(message)=> {
    setStatus(message);
    window.setTimeout(()=> setStatus(''),4500);
  };

  const exportLibrary=(format)=> {
    const content=exportBookmarks(bookmarks,folders,format);
    const date=new Date().toISOString().slice(0,10);
    const config={json:['json','application/json'],html:['html','text/html;charset=utf-8'],csv:['csv','text/csv;charset=utf-8']}[format];
    downloadFile(content,`bookmarks-manager-pro-${date}.${config[0]}`,config[1]);
    notify(`${format.toUpperCase()} export created.`);
  };

  const handleRestoreFile=async (event)=> {
    const file=event.target.files?.[0];
    if (!file) return;
    event.target.value='';
    const lower=file.name.toLowerCase();
    const format=lower.endsWith('.json') ? 'json' : lower.endsWith('.html') || lower.endsWith('.htm') ? 'html' : null;
    if (!format) {
      notify('Choose a Bookmarks Manager JSON backup or browser bookmark HTML file.');
      return;
    }

    const confirmed=window.confirm('Restore adds bookmarks and folders to your current library and skips duplicate URLs. It does not delete or overwrite your existing library. Continue?');
    if (!confirmed) return;

    setBusy(true);
    try {
      const content=await file.text();
      const parsed=await importBookmarks(content,format);
      const result=await importLibrary(parsed);
      notify(`Restore complete: ${result.bookmarksCreated} bookmarks added, ${result.duplicatesSkipped} duplicates skipped, ${result.foldersCreated} folders created.`);
    } catch (error) {
      console.error('Restore failed:',error);
      notify(error.message || 'Restore failed.');
    } finally {
      setBusy(false);
    }
  };

  const copyText=async (text)=> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea=document.createElement('textarea');
    textarea.value=text;
    textarea.style.position='fixed';
    textarea.style.opacity='0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const copyCaptureBookmarklet=async ()=> {
    const appUrl=`${window.location.origin}/`;
    const bookmarklet=`javascript:(()=>{const u=encodeURIComponent(location.href);const t=encodeURIComponent(document.title);window.open('${appUrl}?capture='+u+'&title='+t,'_blank','noopener,noreferrer')})()`;
    try {
      await copyText(bookmarklet);
      setCopiedCapture(true);
      notify('Quick Capture bookmarklet copied. Save it as a browser bookmark.');
      window.setTimeout(()=> setCopiedCapture(false),2500);
    } catch (error) {
      console.error('Failed to copy bookmarklet:',error);
      notify('Could not copy automatically on this browser.');
    }
  };

  const cancelFaviconRefresh=()=> {
    faviconRefreshCancelRef.current=true;
  };

  const handleRefreshFavicons=async ()=> {
    if (faviconProgress.running) return;
    if (!isOnline || navigator.onLine===false) {
      notify('Connect to the internet before refreshing favicons.');
      return;
    }
    if (pendingChanges>0) {
      notify('Sync pending library changes before starting a bulk favicon refresh.');
      return;
    }

    const validBookmarks=(bookmarks || []).filter(bookmark=> {
      if (String(bookmark.id || '').startsWith('temp-')) return false;
      try {return ['http:','https:'].includes(new URL(bookmark.url).protocol);} catch {return false;}
    });
    const manualCount=validBookmarks.filter(bookmark=> bookmark.faviconfile).length;
    const targets=validBookmarks.filter(bookmark=> !bookmark.faviconfile);

    if (!targets.length) {
      notify(manualCount ? 'All eligible bookmarks already use manually uploaded icons.' : 'No eligible bookmark links were found.');
      return;
    }

    const confirmed=window.confirm(`Check ${targets.length} bookmark${targets.length===1 ? '' : 's'} for verified favicons? ${manualCount ? `${manualCount} manually uploaded icon${manualCount===1 ? '' : 's'} will be skipped. ` : ''}Only bookmarks whose verified icon changes will be saved. Those updated records will receive PocketBase's normal modified timestamp.`);
    if (!confirmed) return;

    faviconRefreshCancelRef.current=false;
    let stats={processed:0,updated:0,current:0,unavailable:0};
    setFaviconProgress({running:true,processed:0,total:targets.length,updated:0,current:0,unavailable:0,manual:manualCount,cancelled:false});

    const batchSize=4;
    for (let offset=0;offset<targets.length;offset+=batchSize) {
      if (faviconRefreshCancelRef.current) break;
      const batch=targets.slice(offset,offset+batchSize);
      const results=await Promise.all(batch.map(async bookmark=> {
        try {
          const resolved=await resolveBestFaviconUrl(bookmark.url,{currentUrl:bookmark.faviconurl || '',timeoutMs:2800});
          if (!resolved) return 'unavailable';
          if (resolved===bookmark.faviconurl) return 'current';
          await updateBookmark(bookmark.id,{faviconurl:resolved});
          return 'updated';
        } catch (error) {
          console.warn(`Favicon refresh failed for ${bookmark.url}:`,error);
          return 'unavailable';
        }
      }));

      results.forEach(result=> {stats[result]=(stats[result] || 0)+1;});
      stats.processed+=batch.length;
      setFaviconProgress({running:true,total:targets.length,manual:manualCount,cancelled:false,...stats});
      if (offset+batchSize<targets.length && !faviconRefreshCancelRef.current) await new Promise(resolve=> window.setTimeout(resolve,120));
    }

    const cancelled=faviconRefreshCancelRef.current;
    setFaviconProgress({running:false,total:targets.length,manual:manualCount,cancelled,...stats});
    faviconRefreshCancelRef.current=false;
    if (cancelled) notify(`Favicon refresh stopped after ${stats.processed} of ${targets.length}. ${stats.updated} updated.`);
    else notify(`Favicon refresh complete: ${stats.updated} updated, ${stats.current} already current, ${stats.unavailable} unavailable${manualCount ? `, ${manualCount} manual skipped` : ''}.`);
  };

  const handleReset=async ()=> {
    if (!window.confirm('Reset display and behaviour preferences to their defaults? Your bookmarks and folders are not affected.')) return;
    await resetSettings();
    notify('Preferences reset. Your library was not changed.');
  };

  const showQuickStart=async ()=> {
    await updateSetting('onboardingDismissed',false);
    navigate('/');
  };

  const faviconPercent=faviconProgress.total ? Math.round((faviconProgress.processed/faviconProgress.total)*100) : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8 pb-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={()=> navigate('/')} className="min-h-11 flex items-center gap-2 text-sm font-bold text-zinc-500 hover:text-indigo-500 mb-3"><SafeIcon icon={FiArrowLeft} className="w-4 h-4" />Back to library</button>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Settings</h1>
          <p className="text-sm text-zinc-500 mt-2">Control the experience, portability and capture workflow for your library.</p>
        </div>
        <div className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold ${syncStatus==='offline' || syncStatus==='error' ? 'bg-amber-500/10 text-amber-600' : 'bg-emerald-500/10 text-emerald-600'}`}>
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${syncStatus==='syncing' ? 'animate-spin' : ''}`} />
          {pendingChanges ? `${pendingChanges} pending` : syncStatus==='synced' ? 'Synced' : syncStatus}
        </div>
      </div>

      {status && <div className="p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-sm font-bold text-indigo-700 dark:text-indigo-300" role="status">{status}</div>}

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-5"><SafeIcon icon={FiMonitor} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Appearance</h2><p className="text-sm text-zinc-500">Use only presentation controls that currently affect the app.</p></div></div>
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4"><div><div className="font-bold">Theme</div><p className="text-sm text-zinc-500">Switch between light and dark mode.</p></div><button onClick={toggleTheme} className="min-h-11 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 font-bold">{theme==='dark' ? 'Dark' : 'Light'}</button></div>
          <div className="flex items-center justify-between gap-4"><div><div className="font-bold">High Contrast</div><p className="text-sm text-zinc-500">Increase visual separation for accessibility.</p></div><button onClick={toggleHighContrast} role="switch" aria-checked={highContrast} className={`relative w-12 h-7 rounded-full transition-colors ${highContrast ? 'bg-indigo-600' : 'bg-zinc-300 dark:bg-zinc-700'}`}><span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-1'}`} /></button></div>
        </div>
      </section>

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-5"><SafeIcon icon={FiBookmark} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Library Display</h2><p className="text-sm text-zinc-500">Choose the view and sort order actually used by the library.</p></div></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="space-y-2"><span className="font-bold text-sm">Default View</span><select value={settings.viewMode} onChange={event=> updateSetting('viewMode',event.target.value)} className={`${inputClass} w-full`}><option value="grid">Grid</option><option value="list">List</option><option value="icons">Icons</option></select></label>
          <label className="space-y-2"><span className="font-bold text-sm">Default Sort</span><select value={settings.sortBy} onChange={event=> updateSetting('sortBy',event.target.value)} className={`${inputClass} w-full`}><option value="name">Name</option><option value="dateAdded">Date Added</option><option value="dateModified">Date Modified</option><option value="custom">Custom Order</option></select></label>
        </div>
        <button onClick={()=> updateSetting('showTitles',!settings.showTitles)} className="mt-5 min-h-12 w-full rounded-xl border dark:border-zinc-700 px-4 flex items-center justify-between font-bold"><span>Titles in Icon View</span><span className={settings.showTitles ? 'text-emerald-500' : 'text-zinc-400'}>{settings.showTitles ? 'On' : 'Off'}</span></button>
      </section>

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-5"><SafeIcon icon={FiImage} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Favicon Maintenance</h2><p className="text-sm text-zinc-500">Find verified website/app icons directly from bookmark links and safe favicon services.</p></div></div>
        <div className="p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/10 text-sm text-zinc-500">The resolver checks common site-owned icon paths first—including <strong className="text-zinc-700 dark:text-zinc-300">APP-ICON.svg</strong>, favicon SVG/PNG/ICO and common PWA icons—then falls back to Google and DuckDuckGo. Manually uploaded icons are never replaced.</div>
        {faviconProgress.total>0 && <div className="mt-4 space-y-2" aria-live="polite"><div className="flex items-center justify-between gap-3 text-xs font-bold"><span>{faviconProgress.running ? `Refreshing ${faviconProgress.processed} / ${faviconProgress.total}` : faviconProgress.cancelled ? `Stopped at ${faviconProgress.processed} / ${faviconProgress.total}` : `Last refresh ${faviconProgress.processed} / ${faviconProgress.total}`}</span><span>{faviconPercent}%</span></div><div className="h-2 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden"><div className="h-full bg-indigo-600 transition-[width] duration-200" style={{width:`${faviconPercent}%`}} /></div><div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500"><span>{faviconProgress.updated} updated</span><span>{faviconProgress.current} current</span><span>{faviconProgress.unavailable} unavailable</span>{faviconProgress.manual>0 && <span>{faviconProgress.manual} manual skipped</span>}</div></div>}
        <div className="mt-4 flex flex-col sm:flex-row gap-3"><button onClick={handleRefreshFavicons} disabled={faviconProgress.running || !isOnline || pendingChanges>0} className="min-h-12 flex-1 rounded-xl bg-indigo-600 text-white font-black flex items-center justify-center gap-2 disabled:opacity-45"><SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${faviconProgress.running ? 'animate-spin' : ''}`} />{faviconProgress.running ? 'Refreshing Favicons…' : 'Refresh All Favicons'}</button>{faviconProgress.running && <button onClick={cancelFaviconRefresh} className="min-h-12 px-5 rounded-xl border dark:border-zinc-700 font-bold flex items-center justify-center gap-2"><SafeIcon icon={FiX} className="w-4 h-4" />Stop after batch</button>}</div>
        {(!isOnline || pendingChanges>0) && <p className="mt-3 text-xs font-semibold text-amber-600 dark:text-amber-400">{!isOnline ? 'Connect to the internet to refresh favicons.' : 'Sync pending library changes before running the bulk refresh.'}</p>}
      </section>

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-5"><SafeIcon icon={FiDatabase} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Backup & Portability</h2><p className="text-sm text-zinc-500">Your cloud library stays in PocketBase. These exports give you a portable copy you control.</p></div></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <button onClick={()=> exportLibrary('json')} className="min-h-12 rounded-xl bg-indigo-600 text-white font-bold flex items-center justify-center gap-2"><SafeIcon icon={FiDownload} className="w-4 h-4" />JSON Backup</button>
          <button onClick={()=> exportLibrary('html')} className="min-h-12 rounded-xl border dark:border-zinc-700 font-bold flex items-center justify-center gap-2"><SafeIcon icon={FiFileText} className="w-4 h-4" />Browser HTML</button>
          <button onClick={()=> exportLibrary('csv')} className="min-h-12 rounded-xl border dark:border-zinc-700 font-bold flex items-center justify-center gap-2"><SafeIcon icon={FiDownload} className="w-4 h-4" />CSV</button>
        </div>
        <div className="mt-4 p-4 rounded-xl bg-zinc-500/5 text-sm text-zinc-500"><strong className="text-zinc-700 dark:text-zinc-300">Restore is additive:</strong> it creates imported folders/bookmarks, skips matching duplicate URLs, and does not delete or overwrite your existing library.</div>
        <button onClick={()=> restoreInputRef.current?.click()} disabled={busy} className="mt-4 min-h-12 w-full rounded-xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-black flex items-center justify-center gap-2 disabled:opacity-50"><SafeIcon icon={FiUpload} className="w-4 h-4" />{busy ? 'Restoring…' : 'Restore JSON or Browser HTML'}</button>
        <input ref={restoreInputRef} type="file" accept=".json,.html,.htm,application/json,text/html" className="hidden" onChange={handleRestoreFile} />
      </section>

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-5"><SafeIcon icon={FiSmartphone} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Quick Capture</h2><p className="text-sm text-zinc-500">Save the page you are currently viewing without manually copying its URL.</p></div></div>
        <button onClick={copyCaptureBookmarklet} className="min-h-12 w-full rounded-xl bg-indigo-600 text-white font-black flex items-center justify-center gap-2"><SafeIcon icon={copiedCapture ? FiCheck : FiCopy} className="w-4 h-4" />{copiedCapture ? 'Copied' : 'Copy Quick Capture Bookmarklet'}</button>
        <ol className="mt-4 space-y-2 text-sm text-zinc-500 list-decimal pl-5"><li>Create a browser bookmark named <strong>Save to Bookmarks Pro</strong>.</li><li>Replace its URL with the copied bookmarklet.</li><li>On any webpage, tap/click that bookmark to open Bookmark Manager with the URL prefilled.</li></ol>
        <div className="mt-4 p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/10 text-xs text-zinc-500"><SafeIcon icon={FiInfo} className="w-4 h-4 inline mr-2 text-indigo-500" />The installed PWA also advertises a Web Share Target where the operating system/browser supports it. Platform support varies, so the bookmarklet remains the dependable browser fallback.</div>
      </section>

      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-4"><SafeIcon icon={FiShield} className="w-5 h-5 text-indigo-500" /><div><h2 className="text-lg font-black">Guidance & Reset</h2><p className="text-sm text-zinc-500">Revisit the quick-start guide or reset presentation preferences.</p></div></div>
        <div className="grid sm:grid-cols-2 gap-3"><button onClick={showQuickStart} className="min-h-12 rounded-xl border dark:border-zinc-700 font-bold">Show Quick Start</button><button onClick={handleReset} className="min-h-12 rounded-xl border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 font-bold flex items-center justify-center gap-2"><SafeIcon icon={FiRotateCcw} className="w-4 h-4" />Reset Preferences</button></div>
      </section>
    </div>
  );
};

export default Settings;